/**
 * Main Platform Demo Flow
 *
 * Comprehensive guided tour showcasing the full platform capabilities:
 * 1. Initial conversation with AI assistant
 * 2. Deal creation options
 * 3. Contract upload
 * 4. Auxiliary documents
 * 5. AI analysis & insights
 * 6. Team collaboration
 * 7. Action items & milestones
 * 8. Review & corrections
 * 9. E-signature
 * 10. Delivery management
 */

import { GuidedFlowDefinition } from '../core/types';

export const mainDemoFlow: GuidedFlowDefinition = {
  id: 'main-platform-demo',
  name: 'Equal IQ Platform Demo',
  description: 'Experience the complete deal management workflow from contract upload to delivery',
  version: '1.0.0',
  category: 'demo',
  estimatedDuration: 5, // minutes

  startStepId: 'welcome',

  config: {
    theme: 'dark',
    showProgress: true,
    showStepCounter: false,
    allowSkip: true,
    allowRestart: true,
    autoPlay: true,
    defaultSpeed: 'normal',
    enableKeyboardNav: true,
    trackAnalytics: true,
  },

  steps: [
    // ========================================================================
    // Welcome & Goal Setting
    // ========================================================================
    {
      id: 'welcome',
      type: 'chat',
      title: 'Welcome',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
          role: 'AI Deal Advisor',
        },
        user: {
          name: 'You',
          avatar: undefined,
        },
        messages: [
          {
            id: 'msg-1',
            role: 'assistant',
            content: "Hi! I'm your Equal IQ assistant. I help entertainment professionals manage contracts, deals, and collaborations. What brings you here today?",
            delay: 500,
          },
          {
            id: 'msg-2',
            role: 'user',
            content: "I just received a recording contract from a label and need to understand the terms before signing. It's pretty complex.",
            delay: 2000,
          },
          {
            id: 'msg-3',
            role: 'assistant',
            content: "Perfect! I can help you with that. I'll analyze the contract, break down the key terms in plain language, identify potential risks, and help you manage everything through signing and delivery.",
            delay: 3500,
          },
          {
            id: 'msg-4',
            role: 'user',
            content: "That sounds exactly what I need. How do we start?",
            delay: 5500,
          },
          {
            id: 'msg-5',
            role: 'assistant',
            content: "Great! Let me show you how to create a new deal. There are three ways to get started, and I'll walk you through the best option for your situation.",
            delay: 7000,
            actions: [
              {
                id: 'action-continue',
                label: "Let's go",
                type: 'button',
              },
            ],
          },
        ],
        typingDelay: 300,
        typingDuration: 1000,
      },
      nextStep: 'deal-creation-options',
    },

    // ========================================================================
    // Deal Creation Options
    // ========================================================================
    {
      id: 'deal-creation-options',
      type: 'view',
      title: 'Creating a Deal',
      autoAdvance: false,
      view: {
        component: 'deal-creation',
        assistant: {
          position: 'right',
          message: "You have three ways to create a deal:\n\n**Upload Documents** - Have a PDF or Word doc? Just drag it in.\n\n**Start from Template** - Need to create a new contract? We have pre-built templates for common deal types.\n\n**Meeting Bot** - Had a negotiation call? Our bot can join and automatically extract terms.",
        },
        annotations: [
          {
            id: 'upload-highlight',
            type: 'spotlight',
            target: '.deal-creation-upload',
            content: 'Most common: Upload your existing contract',
            position: 'top',
          },
          {
            id: 'template-highlight',
            type: 'spotlight',
            target: '.deal-creation-template',
            content: 'Start from scratch with expert templates',
            position: 'top',
          },
          {
            id: 'meeting-highlight',
            type: 'spotlight',
            target: '.deal-creation-meeting',
            content: 'AI-powered meeting capture (coming soon)',
            position: 'top',
          },
        ],
        hints: [
          {
            id: 'upload-hint',
            action: 'Click to upload your contract',
            target: '.deal-creation-upload',
            type: 'click',
            animated: true,
          },
        ],
      },
      nextStep: 'upload-contract',
    },

    // ========================================================================
    // Contract Upload
    // ========================================================================
    {
      id: 'upload-contract',
      type: 'interaction',
      title: 'Upload Your Contract',
      autoAdvance: false,
      view: {
        component: 'upload-dialog',
        assistant: {
          position: 'overlay',
          message: "For this demo, I'll upload an Artist Recording Agreement. Just drag and drop your contract PDF or click to browse.",
        },
        annotations: [
          {
            id: 'dropzone',
            type: 'pulse',
            target: '.upload-dropzone',
          },
        ],
        hints: [
          {
            id: 'drag-hint',
            action: 'Drag your contract here',
            target: '.upload-dropzone',
            type: 'drag',
            animated: true,
          },
        ],
      },
      interaction: {
        action: {
          type: 'upload',
          files: [
            {
              name: 'Artist_Recording_Agreement.pdf',
              type: 'application/pdf',
              size: 245600,
              preview: '/demo/contract-preview.png',
              content: 'DEMO_CONTRACT_RECORDING_DEAL',
            },
          ],
        },
        mocked: true,
        mockDelay: 1500,
        onSuccess: {
          message: 'Contract uploaded successfully!',
          celebration: true,
          sound: true,
        },
      },
      nextStep: 'upload-processing',
    },

    // ========================================================================
    // Upload Processing
    // ========================================================================
    {
      id: 'upload-processing',
      type: 'chat',
      title: 'Processing Contract',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-6',
            role: 'assistant',
            content: "Got it! I'm analyzing your recording agreement now. This usually takes 10-15 seconds...",
            delay: 500,
          },
          {
            id: 'msg-7',
            role: 'assistant',
            content: "✨ Analysis complete! I've extracted all the key terms. Do you have any related documents like side letters, amendments, or payment schedules you'd like to add?",
            delay: 3000,
            suggestions: [
              'Yes, I have additional docs',
              'No, just this contract',
            ],
          },
        ],
      },
      nextStep: 'auxiliary-docs-decision',
    },

    // ========================================================================
    // Auxiliary Documents Decision
    // ========================================================================
    {
      id: 'auxiliary-docs-decision',
      type: 'decision',
      title: 'Additional Documents',
      decision: {
        question: 'Would you like to add related documents to this deal?',
        style: 'cards',
        autoSelectInDemo: 'add-docs',
        autoSelectDelay: 2000,
        options: [
          {
            id: 'add-docs',
            label: 'Yes, add documents',
            description: 'Upload side letters, amendments, or schedules',
            icon: 'add',
            nextStep: 'upload-auxiliary',
            recommended: true,
          },
          {
            id: 'skip-docs',
            label: 'No, continue',
            description: 'Proceed with just the main contract',
            icon: 'forward',
            nextStep: 'analysis-intro',
          },
        ],
      },
    },

    // ========================================================================
    // Upload Auxiliary Documents
    // ========================================================================
    {
      id: 'upload-auxiliary',
      type: 'interaction',
      title: 'Additional Documents',
      autoAdvance: false,
      view: {
        component: 'upload-dialog',
        props: {
          mode: 'auxiliary',
          existingFiles: ['Artist_Recording_Agreement.pdf'],
        },
        assistant: {
          position: 'right',
          message: "Great! Adding related documents gives me more context for analysis. Common additions:\n\n• **Side Letters** - Special terms or exceptions\n• **Amendments** - Changes to the original\n• **Payment Schedules** - Detailed payment terms\n• **Exhibits** - Territory rights, marketing plans",
        },
      },
      interaction: {
        action: {
          type: 'upload',
          files: [
            {
              name: 'Side_Letter_Royalties.pdf',
              type: 'application/pdf',
              size: 89200,
            },
            {
              name: 'Payment_Schedule.xlsx',
              type: 'application/vnd.ms-excel',
              size: 45100,
            },
          ],
        },
        mocked: true,
        mockDelay: 1200,
        onSuccess: {
          message: '2 additional documents uploaded',
        },
      },
      nextStep: 'auxiliary-processing',
    },

    // ========================================================================
    // Auxiliary Processing
    // ========================================================================
    {
      id: 'auxiliary-processing',
      type: 'chat',
      title: 'Processing Additional Documents',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-8',
            role: 'assistant',
            content: "Perfect! I'm cross-referencing these documents with your main contract to give you a complete picture...",
            delay: 500,
          },
          {
            id: 'msg-9',
            role: 'assistant',
            content: "✅ All documents analyzed! I found some important modifications in the side letter. Let me show you the full analysis.",
            delay: 2500,
          },
        ],
      },
      nextStep: 'analysis-intro',
    },

    // ========================================================================
    // Analysis Introduction
    // ========================================================================
    {
      id: 'analysis-intro',
      type: 'chat',
      title: 'Understanding Your Deal',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-10',
            role: 'assistant',
            content: "I've broken down your contract into simple cards. You have two view modes:\n\n**EQ Mode** (Plain English) - Key terms explained like a friend would\n**IQ Mode** (Deep Dive) - Detailed legal analysis with Q&A",
            delay: 500,
          },
          {
            id: 'msg-11',
            role: 'user',
            content: "What should I look at first?",
            delay: 2500,
          },
          {
            id: 'msg-12',
            role: 'assistant',
            content: "Start with EQ Mode to get the big picture. I'll highlight any red flags or important items. Here's what I found in your deal...",
            delay: 4000,
            actions: [
              {
                id: 'view-analysis',
                label: 'Show me the analysis',
                type: 'button',
              },
            ],
          },
        ],
      },
      nextStep: 'view-analysis',
    },

    // ========================================================================
    // View Contract Analysis (EQ Mode)
    // ========================================================================
    {
      id: 'view-analysis',
      type: 'view',
      title: 'Contract Analysis',
      autoAdvance: false,
      view: {
        component: 'contract-analysis',
        props: {
          contractId: 'DEMO_CONTRACT_RECORDING_DEAL',
          mode: 'eq',
        },
        assistant: {
          position: 'right',
          message: "Here's your contract in plain English. Each card covers a key aspect:\n\n💰 **Money You Receive** - $150K advance, 50/50 royalty split\n📅 **How Long** - 5 album deal, rights revert after 7 years\n⚠️ **Risks** - Territory exclusivity, recoupment terms\n\nTap any card to see more details or flip it for deeper insights.",
        },
        annotations: [
          {
            id: 'money-card',
            type: 'highlight',
            target: '[data-card="moneyYouReceive"]',
            content: 'Key financial terms',
            position: 'top',
          },
          {
            id: 'risk-flag',
            type: 'pulse',
            target: '[data-card="risksCostsLegalStuff"]',
            content: '⚠️ Review carefully',
            position: 'top',
            style: {
              color: '#ff6b6b',
            },
          },
          {
            id: 'flip-hint',
            type: 'arrow',
            target: '[data-tour="flip-card"]',
            content: 'Flip for IQ Mode',
            position: 'left',
          },
        ],
      },
      nextStep: 'analysis-insights',
    },

    // ========================================================================
    // AI Insights & Recommendations
    // ========================================================================
    {
      id: 'analysis-insights',
      type: 'chat',
      title: 'Key Insights',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-13',
            role: 'user',
            content: "The advance looks good, but I'm worried about the recoupment terms. What do you think?",
            delay: 500,
          },
          {
            id: 'msg-14',
            role: 'assistant',
            content: "Good catch! Here's what I found:\n\n🚩 **Recoupment at 100%** - You won't see royalties until the full $150K is recouped\n📊 **Compared to similar deals** - Industry standard is 75-85% recoupment\n💡 **Recommendation** - Negotiate for 75% recoupment or a shorter recoupment period\n\nWould you like me to flag this for your legal team to review?",
            delay: 2000,
            suggestions: [
              'Yes, flag for review',
              'No, looks fine',
            ],
          },
        ],
      },
      nextStep: 'collaboration-intro',
    },

    // ========================================================================
    // Team Collaboration Introduction
    // ========================================================================
    {
      id: 'collaboration-intro',
      type: 'chat',
      title: 'Team Collaboration',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-15',
            role: 'assistant',
            content: "I've flagged the recoupment issue. Now let's get your team involved. You can:\n\n👥 **Share with advisors** - Your lawyer, manager, or business affairs team\n💬 **Add comments** - Discuss specific clauses\n📌 **Assign tasks** - 'Sarah, review Section 4.2'\n🔔 **Get notifications** - When someone reviews or comments",
            delay: 500,
          },
          {
            id: 'msg-16',
            role: 'user',
            content: "Can I share this with my lawyer and manager?",
            delay: 2500,
          },
          {
            id: 'msg-17',
            role: 'assistant',
            content: "Absolutely! I'll show you how to invite them and set up the collaboration workspace.",
            delay: 4000,
            actions: [
              {
                id: 'setup-collab',
                label: 'Set up collaboration',
                type: 'button',
              },
            ],
          },
        ],
      },
      nextStep: 'collaboration-view',
    },

    // ========================================================================
    // Collaboration Workspace
    // ========================================================================
    {
      id: 'collaboration-view',
      type: 'view',
      title: 'Collaboration',
      autoAdvance: false,
      view: {
        component: 'deal-page',
        props: {
          dealId: 'DEMO_DEAL_RECORDING',
          tab: 'overview',
        },
        assistant: {
          position: 'right',
          message: "This is your deal workspace. Everyone you invite can:\n\n✅ View the contract analysis\n💬 Comment on specific terms\n🎯 Complete assigned tasks\n📎 Add additional documents\n\nAll activity is tracked so you know who's reviewed what.",
        },
        annotations: [
          {
            id: 'participants',
            type: 'spotlight',
            target: '[data-section="participants"]',
            content: 'Invite team members',
          },
          {
            id: 'activity',
            type: 'spotlight',
            target: '[data-section="activity"]',
            content: 'See all updates',
          },
          {
            id: 'action-items',
            type: 'spotlight',
            target: '[data-section="action-items"]',
            content: 'Track todos',
          },
        ],
      },
      nextStep: 'action-items-intro',
    },

    // ========================================================================
    // Action Items & Milestones
    // ========================================================================
    {
      id: 'action-items-intro',
      type: 'chat',
      title: 'Action Items',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-18',
            role: 'assistant',
            content: "I automatically identified action items and milestones from your contract:\n\n📋 **Action Items:**\n• Review recoupment terms (High priority)\n• Verify territory exclusivity (Medium)\n• Confirm delivery deadlines (Low)\n\n🎯 **Milestones:**\n• Master recording delivery - June 1, 2025\n• First single release - July 15, 2025\n• Album release - Sept 1, 2025\n\nWant to see the full timeline?",
            delay: 500,
            actions: [
              {
                id: 'view-timeline',
                label: 'Show timeline',
                type: 'button',
              },
            ],
          },
        ],
      },
      nextStep: 'action-items-view',
    },

    // ========================================================================
    // Action Items View
    // ========================================================================
    {
      id: 'action-items-view',
      type: 'view',
      title: 'Tasks & Milestones',
      autoAdvance: false,
      view: {
        component: 'deal-page',
        props: {
          dealId: 'DEMO_DEAL_RECORDING',
          tab: 'overview',
          focusSection: 'action-items',
        },
        assistant: {
          position: 'bottom',
          message: "Each action item can be assigned to team members with due dates. Check items off as you complete them. Milestones automatically appear in your calendar and you'll get reminders.",
        },
        annotations: [
          {
            id: 'high-priority',
            type: 'pulse',
            target: '[data-priority="high"]',
            content: 'High priority - needs attention',
            style: {
              color: '#ff6b6b',
            },
          },
          {
            id: 'assign-button',
            type: 'arrow',
            target: '[data-action="assign"]',
            content: 'Assign to team member',
          },
        ],
      },
      nextStep: 'review-corrections',
    },

    // ========================================================================
    // Review & Corrections
    // ========================================================================
    {
      id: 'review-corrections',
      type: 'chat',
      title: 'Review & Corrections',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-19',
            role: 'user',
            content: "My lawyer says the recoupment should be 75%, not 100%. Can we update that?",
            delay: 500,
          },
          {
            id: 'msg-20',
            role: 'assistant',
            content: "Absolutely! You can update any terms I extracted. I'll keep track of what changed and flag it for the counterparty. This is helpful for:\n\n✏️ Fixing extraction errors\n📝 Noting negotiated changes\n🔄 Tracking versions\n\nWould you like to edit the recoupment terms now?",
            delay: 2000,
            actions: [
              {
                id: 'edit-terms',
                label: 'Edit terms',
                type: 'button',
              },
              {
                id: 'skip-edit',
                label: 'Skip for now',
                type: 'button',
              },
            ],
          },
        ],
      },
      nextStep: 'signature-intro',
    },

    // ========================================================================
    // E-Signature Introduction
    // ========================================================================
    {
      id: 'signature-intro',
      type: 'chat',
      title: 'Ready to Sign',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-21',
            role: 'assistant',
            content: "Great! Your team has reviewed everything and approved. Ready to sign? \n\nI integrate with DocuSign and Adobe Sign, or you can sign directly on the platform with our built-in e-signature tool.\n\nAll signatures are legally binding and automatically saved to your deal file.",
            delay: 500,
          },
          {
            id: 'msg-22',
            role: 'user',
            content: "Let's sign it on the platform.",
            delay: 2500,
          },
          {
            id: 'msg-23',
            role: 'assistant',
            content: "Perfect! I'll prepare the signature fields...",
            delay: 4000,
          },
        ],
      },
      nextStep: 'signature-view',
    },

    // ========================================================================
    // E-Signature View
    // ========================================================================
    {
      id: 'signature-view',
      type: 'interaction',
      title: 'Sign Contract',
      autoAdvance: false,
      view: {
        component: 'contract-analysis',
        props: {
          contractId: 'DEMO_CONTRACT_RECORDING_DEAL',
          mode: 'signature',
        },
        assistant: {
          position: 'right',
          message: "Review one more time and sign where indicated. I've highlighted all signature fields. Once you sign:\n\n✅ Contract is locked\n📧 Counterparty is notified\n📁 Signed copy is saved\n🔔 Delivery milestones activate",
        },
        annotations: [
          {
            id: 'signature-field',
            type: 'pulse',
            target: '[data-field="signature"]',
            content: 'Click to sign',
          },
        ],
        hints: [
          {
            id: 'sign-hint',
            action: 'Click to sign here',
            target: '[data-field="signature"]',
            type: 'click',
            animated: true,
          },
        ],
      },
      interaction: {
        action: {
          type: 'click',
          clickTarget: '[data-field="signature"]',
        },
        mocked: true,
        mockDelay: 2000,
        onSuccess: {
          message: '✨ Contract signed successfully!',
          celebration: true,
          sound: true,
        },
      },
      nextStep: 'delivery-intro',
    },

    // ========================================================================
    // Delivery Management Introduction
    // ========================================================================
    {
      id: 'delivery-intro',
      type: 'chat',
      title: 'Managing Delivery',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-24',
            role: 'assistant',
            content: "🎉 Congratulations! Your contract is signed. Now the real work begins.\n\nI'll help you manage:\n\n📦 **Deliverables** - Track what's due and when\n💰 **Payments** - Monitor advance payments and royalties\n🔔 **Reminders** - Get notified before deadlines\n📊 **Progress** - See deal status at a glance",
            delay: 500,
          },
          {
            id: 'msg-25',
            role: 'user',
            content: "This is amazing! So everything is tracked automatically?",
            delay: 2500,
          },
          {
            id: 'msg-26',
            role: 'assistant',
            content: "Exactly! I extracted all deliverables, payment schedules, and milestones from your contract. You'll get reminders, and your team can mark items complete as you deliver. Let me show you...",
            delay: 4500,
            actions: [
              {
                id: 'view-delivery',
                label: 'Show delivery dashboard',
                type: 'button',
              },
            ],
          },
        ],
      },
      nextStep: 'delivery-view',
    },

    // ========================================================================
    // Delivery Dashboard
    // ========================================================================
    {
      id: 'delivery-view',
      type: 'view',
      title: 'Delivery Management',
      autoAdvance: false,
      view: {
        component: 'deal-page',
        props: {
          dealId: 'DEMO_DEAL_RECORDING',
          tab: 'overview',
          focusSection: 'deliverables',
        },
        assistant: {
          position: 'right',
          message: "Your delivery dashboard shows:\n\n✅ **Completed** - Signed contract\n⏳ **Upcoming** - Master recording (due June 1)\n💵 **Payments** - $50K advance (received), $100K (pending delivery)\n📅 **Timeline** - Visual calendar of all milestones\n\nClick any item to see details or mark as complete.",
        },
        annotations: [
          {
            id: 'completed-items',
            type: 'highlight',
            target: '[data-status="completed"]',
            content: 'Already done!',
          },
          {
            id: 'upcoming-deadline',
            type: 'pulse',
            target: '[data-deadline-upcoming="true"]',
            content: 'Due soon',
            style: {
              color: '#ffd93d',
            },
          },
          {
            id: 'payment-tracking',
            type: 'spotlight',
            target: '[data-section="payments"]',
            content: 'Payment status',
          },
        ],
      },
      nextStep: 'demo-complete',
    },

    // ========================================================================
    // Demo Complete
    // ========================================================================
    {
      id: 'demo-complete',
      type: 'celebration',
      title: 'Demo Complete!',
      autoAdvance: false,
      chat: {
        assistant: {
          name: 'Equal IQ Assistant',
          avatar: '/img/logo-40.svg',
        },
        user: {
          name: 'You',
        },
        messages: [
          {
            id: 'msg-27',
            role: 'assistant',
            content: "🎊 You've completed the Equal IQ platform tour!\n\nYou learned how to:\n✅ Upload and analyze contracts\n✅ Add auxiliary documents\n✅ Collaborate with your team\n✅ Track action items and milestones\n✅ Sign contracts on platform\n✅ Manage delivery and payments\n\n**Next Steps:**\n• Start your free pilot with real contracts\n• Invite your team\n• Connect your tools (Slack, Google Calendar, QuickBooks)\n\nReady to transform your deal management?",
            delay: 500,
            actions: [
              {
                id: 'start-pilot',
                label: 'Start Free Pilot',
                type: 'button',
              },
              {
                id: 'replay-demo',
                label: 'Replay Demo',
                type: 'button',
              },
              {
                id: 'explore-app',
                label: 'Explore on My Own',
                type: 'button',
              },
            ],
          },
        ],
      },
    },
  ],

  // ============================================================================
  // Lifecycle Hooks
  // ============================================================================

  onStart: async (context) => {
    console.log('[Demo] Starting main platform demo', context);

    // Track demo start
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('demo_started', {
        flowId: context.flowId,
        mode: context.mode,
      });
    }
  },

  onComplete: async (context) => {
    console.log('[Demo] Demo completed!', context);

    // Track completion
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('demo_completed', {
        flowId: context.flowId,
        duration: Date.now() - context.startTime.getTime(),
        stepsCompleted: context.completedSteps.length,
      });
    }

    // Show completion modal or redirect
    // TO BE IMPLEMENTED: Show CTA for pilot signup
  },

  onCancel: async (context) => {
    console.log('[Demo] Demo cancelled', context);

    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('demo_cancelled', {
        flowId: context.flowId,
        lastStep: context.currentStepId,
        progress: (context.completedSteps.length / context.totalSteps) * 100,
      });
    }
  },

  onStepChange: async (context, step) => {
    console.log('[Demo] Step changed:', step.id);

    // Update URL to reflect current step (for deep linking)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('step', step.id);
      window.history.replaceState({}, '', url.toString());
    }
  },
};
